/**
 * ONE <video> element for every moment this app plays (BUG-076).
 *
 * iOS grants "this element may make noise" per ELEMENT, from a real gesture,
 * and it keeps that grant through `src` swaps and through being moved to
 * another parent. It does NOT grant it to a fresh element. The old viewer
 * mounted a new <video> for every video moment, so a photo between two videos
 * threw the permission away and the next video came out silent — the coin
 * flip Nation's wife hit.
 *
 * So: one module-singleton element, adopted by whichever player is on screen,
 * unlocked once by the tap that opens the moment.
 *
 * `unlockMomentAudio()` MUST be called synchronously inside a real gesture
 * handler (no await, no setTimeout in front of it) — iOS spends the activation
 * on the first `await`, which is why the tap that opens a moment couldn't be
 * used from inside the viewer's async load.
 */

/**
 * 0.02s of silence, AAC in MP4 (860 bytes). Playing this from the gesture is
 * what buys the grant; the real moment's bytes arrive later on the same
 * element and inherit it.
 */
const SILENT_MP4 =
  'data:video/mp4;base64,AAAAHGZ0eXBpc29tAAACAGlzb21pc28ybXA0MQAAAxdtb292AAAAbG12aGQAAAAAAAAAAAAAAAAAAAPoAAAAFAABAAABAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACAAACQXRyYWsAAABcdGtoZAAAAAMAAAAAAAAAAAAAAAEAAAAAAAAAFAAAAAAAAAAAAAAAAQEAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAACRlZHRzAAAAHGVsc3QAAAAAAAAAAQAAABQAAAQAAAEAAAAAAbltZGlhAAAAIG1kaGQAAAAAAAAAAAAAAAAAAB9AAAAEoFXEAAAAAAAtaGRscgAAAAAAAAAAc291bgAAAAAAAAAAAAAAAFNvdW5kSGFuZGxlcgAAAAFkbWluZgAAABBzbWhkAAAAAAAAAAAAAAAkZGluZgAAABxkcmVmAAAAAAAAAAEAAAAMdXJsIAAAAAEAAAEoc3RibAAAAH5zdHNkAAAAAAAAAAEAAABubXA0YQAAAAAAAAABAAAAAAAAAAAAAQAQAAAAAB9AAAAAAAA2ZXNkcwAAAAADgICAJQABAASAgIAXQBUAAAAAABdwAAAFRwWAgIAFFYhW5QAGgICAAQIAAAAUYnRydAAAAAAAABdwAAAFRwAAACBzdHRzAAAAAAAAAAIAAAABAAAEAAAAAAEAAACgAAAAHHN0c2MAAAAAAAAAAQAAAAEAAAACAAAAAQAAABxzdHN6AAAAAAAAAAAAAAACAAAAFQAAAAQAAAAUc3RjbwAAAAAAAAABAAADQwAAABpzZ3BkAQAAAHJvbGwAAAACAAAAAf//AAAAHHNiZ3AAAAAAcm9sbAAAAAEAAAACAAAAAQAAAGJ1ZHRhAAAAWm1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAG1kaXJhcHBsAAAAAAAAAAAAAAAALWlsc3QAAAAlqXRvbwAAAB1kYXRhAAAAAQAAAABMYXZmNjIuMTIuMTAyAAAACGZyZWUAAAAhbWRhdN4CAExhdmM2Mi4yOC4xMDIAAjBADgEYIAc=';

let element: HTMLVideoElement | null = null;
let owner: HTMLElement | null = null;
let unlocked = false;

/** Fire and forget: play() is a promise in browsers, undefined in jsdom. */
function tryPlay(video: HTMLVideoElement): void {
  try {
    const result = video.play() as unknown as Promise<void> | undefined;
    if (result && typeof result.catch === 'function') result.catch(() => {});
  } catch {
    /* nothing playing beats a thrown error */
  }
}

/** The one element. Created on first use, never replaced. */
export function getSharedMomentVideo(): HTMLVideoElement {
  if (!element) {
    const video = document.createElement('video');
    video.playsInline = true;
    // iOS < 10 spelling; harmless everywhere else, and the property alone is
    // not always honoured on an element built outside React.
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.preload = 'auto';
    element = video;
  }
  return element;
}

/** Has the element been through a gesture yet? */
export function isMomentAudioUnlocked(): boolean {
  return unlocked;
}

/**
 * Spend a real user gesture on the shared element so later moments can start
 * with sound. Muted play + unmute in the SAME tick is the iOS-safe form.
 * Idempotent — the grant is permanent for the element's lifetime.
 */
export function unlockMomentAudio(): void {
  if (unlocked) return;
  unlocked = true;

  const video = getSharedMomentVideo();
  try {
    if (!video.getAttribute('src')) video.src = SILENT_MP4;
    video.muted = true;
    tryPlay(video);
    // Same tick, still inside the gesture: this is the unmute that counts.
    video.muted = false;
  } catch {
    /* an unlock that fails just leaves us where we already were */
  }
}

/**
 * Put the shared element inside `host`. The last caller wins — a re-parent
 * keeps the grant, which is the whole point.
 */
export function adoptSharedVideo(host: HTMLElement): HTMLVideoElement {
  const video = getSharedMomentVideo();
  owner = host;
  if (video.parentElement !== host) host.appendChild(video);
  return video;
}

/**
 * Hand the element back. Guarded by the host: ProtectedRoute double-mounts, so
 * mount A's teardown runs AFTER mount B has already adopted — without this
 * check A would rip the element out of B's screen.
 *
 * Clears `src` and reloads so the element is holding no blob URL by the time
 * the caller's BlobLru revokes them.
 */
export function releaseSharedVideo(host: HTMLElement): void {
  if (owner !== host) return;
  owner = null;

  const video = element;
  if (!video) return;
  try {
    video.removeAttribute('src');
    video.load();
  } catch {
    /* jsdom has no load(); the detach below is what matters */
  }
  video.parentElement?.removeChild(video);
}

/**
 * Sound on or off, NOW — called from inside the gesture that asked for it.
 *
 * Every viewer used to own its own copy of this: some set React state and let
 * an effect apply it a render later, which iOS does not honour (the activation
 * window is gone by then), and one read `videoRef.current.muted` as its source
 * of truth, so with no element the button did nothing at all (BUG-107). One
 * function, on the module that owns the element, called synchronously in the
 * handler. It also doubles as the "sound isn't working" rescue: unmuting
 * replays a clip that an autoplay refusal left muted and stopped.
 *
 * Replaces `recoverMomentSound`, which could only ever turn sound back ON.
 */
export function setMomentSound(soundOn: boolean): void {
  applyMomentSound(element, soundOn);
}

/**
 * The same rule against an element the caller owns — the story viewer plays its
 * own `<video>` rather than the shared one, and it had the identical defect.
 * A null element is a no-op, never a throw.
 */
export function applyMomentSound(
  video: HTMLVideoElement | null,
  soundOn: boolean
): void {
  if (!video) return;
  video.muted = !soundOn;
  if (!soundOn) return;
  // Nothing loaded = nothing to rescue. The viewer's own paused state is
  // MomentVideo's business; this only restarts what stopped.
  if (!video.getAttribute('src')) return;
  if (video.paused) tryPlay(video);
}

/** Tests only: forget the singleton so each case starts clean. */
export function resetSharedMomentVideo(): void {
  element?.parentElement?.removeChild(element);
  element = null;
  owner = null;
  unlocked = false;
}
